/**
 * Sales Follow-Up Intelligence Engine
 * 
 * ⚠️  INTERNAL MODULE — Do not import directly from application code.
 *     Use `unifiedFollowUp.ts` as the single entry point.
 * 
 * MODULE OWNERSHIP: Follow-up domain — sales context (see moduleOwnership.ts)
 * RESPONSIBILITY: Disposition-based follow-up defaults, scripts, and sequences
 * for post-appointment re-engagement.
 * 
 * ENFORCEMENT: This module is guarded by assertInternalAccess().
 * Unauthorized imports will throw in DEV and warn in PROD.
 */

/** @internal — Marker for architecture enforcement. Do not remove. */
export const INTERNAL_ONLY = true as const;
export const INTERNAL_FACADE = "unifiedFollowUp.ts" as const;

import type { DispositionReasonLabel } from "./opportunityTransitions";

export type ExecutionMethod = "call" | "text" | "call_and_text";
export type FollowUpPriority = "high" | "medium" | "low";
export type TouchOutcome = "re_engaged" | "no_response" | "closed_won" | "closed_lost" | "pending";

export interface FollowUpDefaults {
  dueDays: number;
  method: ExecutionMethod;
  priority: FollowUpPriority;
  script: string;
  sequence: FollowUpTouch[];
}

export interface FollowUpTouch {
  dayOffset: number;
  method: ExecutionMethod;
  label: string;
  script: string;
  status: "pending" | "completed" | "skipped";
  completedAt?: string;
  outcome?: TouchOutcome;
}

export const EXECUTION_METHOD_LABELS: Record<ExecutionMethod, string> = {
  call: "Phone Call",
  text: "Text Message",
  call_and_text: "Call + Text",
};

export const PRIORITY_CONFIG: Record<FollowUpPriority, { label: string; color: string; bg: string }> = {
  high:   { label: "High",   color: "text-destructive",     bg: "bg-destructive/10" },
  medium: { label: "Medium", color: "text-warning",         bg: "bg-warning/10" },
  low:    { label: "Low",    color: "text-muted-foreground", bg: "bg-muted/30" },
};

export const TOUCH_OUTCOME_LABELS: Record<TouchOutcome, string> = {
  re_engaged: "Re-Engaged",
  no_response: "No Response",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
  pending: "Pending",
};

function buildScript(name: string, context: string): string {
  return `Hey ${name}, this is {rep} from Creative Concepts Contracting. ${context}`;
}

const DISPOSITION_DEFAULTS: Record<string, Omit<FollowUpDefaults, "script" | "sequence">> = {
  "Price too high":          { dueDays: 2, method: "call_and_text", priority: "high" },
  "Need to think about it":  { dueDays: 1, method: "text",          priority: "high" },
  "Comparing competitors":   { dueDays: 1, method: "call",          priority: "high" },
  "No decision maker present": { dueDays: 0, method: "call",        priority: "high" },
  "Timing not right":        { dueDays: 7, method: "text",          priority: "medium" },
  "Project postponed":       { dueDays: 14, method: "text",         priority: "low" },
  "Financing concerns":      { dueDays: 2, method: "call_and_text", priority: "high" },
  "Went with competitor":    { dueDays: 5, method: "text",          priority: "low" },
  "Not qualified":           { dueDays: 7, method: "text",          priority: "low" },
  "Other":                   { dueDays: 3, method: "call",          priority: "medium" },
};

const DISPOSITION_SCRIPTS: Record<string, (name: string) => string> = {
  "Price too high": (name) =>
    buildScript(name, "I wanted to follow up on your project. We have some flexible options that might work within your budget — would you have a few minutes to chat?"),
  "Need to think about it": (name) =>
    buildScript(name, "Just checking in on your project. I know it's a big decision — if you have any questions at all, I'm here to help."),
  "Comparing competitors": (name) =>
    buildScript(name, "I wanted to touch base about your project. If you're comparing options, I'd love the chance to review what makes our approach different. Do you have a few minutes?"),
  "No decision maker present": (name) =>
    buildScript(name, "I stopped by earlier but wasn't able to connect with everyone. When would be a good time for all decision makers to be available?"),
  "Timing not right": (name) =>
    buildScript(name, "Just a friendly check-in on your project. Whenever the timing is right, we'd love to help. No rush at all."),
  "Project postponed": (name) =>
    buildScript(name, "Hope you're doing well! Just wanted to stay in touch regarding your project. Whenever you're ready to revisit, we're here."),
  "Financing concerns": (name) =>
    buildScript(name, "I wanted to follow up — we have several financing programs that may work for your situation. Would you like to explore some options together?"),
  "Went with competitor": (name) =>
    buildScript(name, "I hope your project is going well. If you ever need anything in the future or have any concerns with your current project, don't hesitate to reach out."),
  "Not qualified": (name) =>
    buildScript(name, "Thank you for your time. If your situation changes or you have questions down the road, feel free to reach out anytime."),
  "Other": (name) =>
    buildScript(name, "Just following up on our recent conversation. Please don't hesitate to reach out if you have any questions."),
};

function buildSequence(reason: string, name: string): FollowUpTouch[] {
  const defaults = DISPOSITION_DEFAULTS[reason] || DISPOSITION_DEFAULTS["Other"];
  const scriptFn = DISPOSITION_SCRIPTS[reason] || DISPOSITION_SCRIPTS["Other"];

  const touches: FollowUpTouch[] = [
    {
      dayOffset: defaults.dueDays,
      method: defaults.method,
      label: "Initial Follow-Up",
      script: scriptFn(name),
      status: "pending",
    },
  ];

  if (["Price too high", "Financing concerns"].includes(reason)) {
    touches.push(
      {
        dayOffset: defaults.dueDays + 3,
        method: "text",
        label: "Value Reminder",
        script: buildScript(name, "Just a quick reminder — our current promotion is still available. Let me know if you'd like to revisit your options."),
        status: "pending",
      },
      {
        dayOffset: defaults.dueDays + 8,
        method: "call",
        label: "Final Touch",
        script: buildScript(name, "I wanted to reach out one more time about your project. We're here whenever you're ready."),
        status: "pending",
      },
    );
  } else if (["Need to think about it", "Comparing competitors"].includes(reason)) {
    touches.push(
      {
        dayOffset: defaults.dueDays + 3,
        method: "text",
        label: "Check-In",
        script: buildScript(name, "Just checking in — have you had a chance to think things over? Happy to answer any questions."),
        status: "pending",
      },
      {
        dayOffset: defaults.dueDays + 7,
        method: "call_and_text",
        label: "Final Outreach",
        script: buildScript(name, "Wanted to reach out one last time. If now isn't the right time, no worries — we're always here when you're ready."),
        status: "pending",
      },
    );
  } else if (reason === "No decision maker present") {
    touches.push({
      dayOffset: defaults.dueDays + 2,
      method: "call",
      label: "Reschedule Attempt",
      script: buildScript(name, "I'd love to set up a time when everyone can be available. What works best for your household?"),
      status: "pending",
    });
  }

  return touches;
}

export function getFollowUpDefaults(reason: string, customerName: string): FollowUpDefaults {
  const base = DISPOSITION_DEFAULTS[reason] || DISPOSITION_DEFAULTS["Other"];
  const scriptFn = DISPOSITION_SCRIPTS[reason] || DISPOSITION_SCRIPTS["Other"];

  return {
    ...base,
    script: scriptFn(customerName),
    sequence: buildSequence(reason, customerName),
  };
}

export function getConfigDrivenDefaults(
  reason: string,
  customerName: string,
  repName: string,
  config: {
    rules: Array<{ dispositionName: string; defaultFollowUpDays: number; executionMethod: ExecutionMethod; priorityLevel: FollowUpPriority; active: boolean; autoSendThankYou: boolean }>;
    templates: Array<{ id: string; dispositionRuleId: string; type: string; body: string; autoSend: boolean; label: string }>;
    sequences: Array<{ dispositionRuleId: string; dayOffset: number; actionType: ExecutionMethod; templateId: string; autoSend: boolean; label: string; order: number }>;
    globalSettings: { thankYouDelayMinutes: number };
  },
): FollowUpDefaults & { thankYouMessage?: string; thankYouDelayMinutes: number } {
  const rule = config.rules.find(r => r.dispositionName === reason && r.active);
  if (!rule) return { ...getFollowUpDefaults(reason, customerName), thankYouDelayMinutes: config.globalSettings.thankYouDelayMinutes };

  const ruleId = (rule as any).id || "";
  const ruleTemplates = config.templates.filter(t => t.dispositionRuleId === ruleId);
  const ruleSequence = config.sequences.filter(s => s.dispositionRuleId === ruleId).sort((a, b) => a.order - b.order);

  const resolveVars = (body: string) =>
    body.replace(/\{\{customer_name\}\}/g, customerName.split(" ")[0])
        .replace(/\{\{rep_name\}\}/g, repName)
        .replace(/\{\{project_type\}\}/g, "your project");

  const mainTemplate = ruleTemplates.find(t => t.type === "follow_up_1") || ruleTemplates[0];
  const script = mainTemplate ? resolveVars(mainTemplate.body) : getFollowUpDefaults(reason, customerName).script;

  const sequence: FollowUpTouch[] = ruleSequence.map(step => {
    const tpl = config.templates.find(t => t.id === step.templateId);
    return {
      dayOffset: step.dayOffset,
      method: step.actionType,
      label: step.label,
      script: tpl ? resolveVars(tpl.body) : script,
      status: "pending" as const,
    };
  });

  const thankYouTpl = ruleTemplates.find(t => t.type === "thank_you");
  const thankYouMessage = thankYouTpl ? resolveVars(thankYouTpl.body) : (rule.autoSendThankYou ? getThankYouMessage(customerName) : undefined);

  return {
    dueDays: rule.defaultFollowUpDays,
    method: rule.executionMethod,
    priority: rule.priorityLevel,
    script,
    sequence: sequence.length > 0 ? sequence : buildSequence(reason, customerName),
    thankYouMessage,
    thankYouDelayMinutes: config.globalSettings.thankYouDelayMinutes,
  };
}

export function calculateDueDate(daysOffset: number): string {
  const d = new Date();
  if (daysOffset === 0) return d.toISOString().slice(0, 10);
  let added = 0;
  while (added < daysOffset) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

export function getThankYouMessage(customerName: string): string {
  const firstName = customerName.split(" ")[0];
  return `Hey ${firstName}, thank you again for taking the time to meet with me today. It was great learning about your project. If any questions come up, don't hesitate to reach out — I'm here to help.`;
}
